import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { useSession } from '../session/SessionProvider.js'
import '../styles/forms.css'

/**
 * I documenti allegati a qualcosa: un soggetto, una persona, una denuncia.
 *
 * È un componente unico per tutti e quattro i tipi di proprietario perché
 * l'archivio è uno solo: cambia il `owner_type`, non il modo di leggerlo o di
 * scriverlo. Duplicarlo per la scheda del soggetto e per quella della persona
 * significherebbe due posti in cui correggere lo stesso limite di dimensione.
 *
 * **Il file si sceglie e parte.** Non c'è un pulsante «carica» separato dalla
 * scelta: chi allega una scansione ha già deciso quando ha scelto il file, e
 * un secondo gesto serve solo a far dimenticare il primo.
 *
 * **Lo scaricamento passa da una richiesta autenticata**, non da un
 * collegamento: i file stanno fuori dalla radice del sito apposta, e un
 * `<a href>` non porterebbe il token.
 */
export function Documenti({
  ownerType,
  ownerId,
  titolo = 'Documenti',
}: {
  ownerType: 'animal' | 'person' | 'declaration' | 'litter'
  ownerId: number
  titolo?: string
}) {
  const { resources } = useSession()
  const cache = useQueryClient()
  const campo = useRef<HTMLInputElement>(null)
  const [errore, setErrore] = useState<string | null>(null)

  const elenco = useQuery({
    queryKey: ['documents', ownerType, ownerId],
    queryFn: ({ signal }) => resources.documents.of(ownerType, ownerId, signal),
  })

  const carica = useMutation({
    mutationFn: (file: File) =>
      resources.documents.upload({ file, ownerType, ownerId, title: file.name }),
    onMutate: () => {
      setErrore(null)
    },
    onSuccess: () => {
      // Il campo si svuota: lasciarci dentro il file appena caricato fa
      // credere che non sia partito.
      if (campo.current) campo.current.value = ''
      void cache.invalidateQueries({ queryKey: ['documents', ownerType, ownerId] })
    },
    onError: (e: unknown) => {
      const problema = e as {
        status?: number
        violations?: readonly { propertyPath: string; message: string }[]
      }

      // Il motivo del rifiuto è l'informazione utile: «tipo non ammesso» e
      // «troppo grande» si correggono in due modi diversi.
      setErrore(
        problema.violations?.[0]?.message ??
          (problema.status === 403
            ? 'Il vostro profilo non può allegare documenti.'
            : 'Non è stato possibile caricare il file.'),
      )
    },
  })

  const scarica = useMutation({
    mutationFn: (id: number) => resources.documents.download(id),
    onSuccess: ({ blob, filename }) => {
      const indirizzo = URL.createObjectURL(blob)
      const collegamento = document.createElement('a')

      collegamento.href = indirizzo
      collegamento.download = filename
      collegamento.click()
      URL.revokeObjectURL(indirizzo)
    },
    onError: () => {
      setErrore('Non è stato possibile scaricare il file.')
    },
  })

  const elimina = useMutation({
    mutationFn: (id: number) => resources.documents.remove(id),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['documents', ownerType, ownerId] })
    },
    onError: () => {
      setErrore('Non è stato possibile eliminare il documento.')
    },
  })

  return (
    <section className="storico" aria-labelledby={`titolo-documenti-${ownerType}`}>
      <div className="elenco__testa">
        <h2 id={`titolo-documenti-${ownerType}`}>{titolo}</h2>
        <label className="documenti__scelta">
          <span className="solo-lettore">Allega un documento</span>
          <input
            ref={campo}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.tif,.tiff"
            disabled={carica.isPending}
            onChange={(evento) => {
              const file = evento.target.files?.[0]

              if (file) carica.mutate(file)
            }}
          />
        </label>
      </div>

      {carica.isPending && <p className="messaggio">Caricamento in corso…</p>}

      {errore && (
        <p role="alert" className="messaggio messaggio--errore">
          {errore}
        </p>
      )}

      {elenco.data?.member.length === 0 && !carica.isPending && (
        <p className="messaggio">Nessun documento allegato.</p>
      )}

      {elenco.data && elenco.data.member.length > 0 && (
        <ul className="storico__righe">
          {elenco.data.member.map((documento) => (
            <li key={documento.id} className="storico__riga documenti__riga">
              <span className="riga__nome">{documento.title}</span>
              <span className="riga__secondario">
                {documento.type?.label ?? tipoLeggibile(documento.mime_type)} ·{' '}
                {dimensione(documento.size_bytes)}
              </span>
              <span className="documenti__azioni">
                <button
                  type="button"
                  data-variante="piatto"
                  disabled={scarica.isPending}
                  onClick={() => {
                    scarica.mutate(documento.id)
                  }}
                >
                  Scarica
                </button>
                <button
                  type="button"
                  data-variante="piatto"
                  disabled={elimina.isPending}
                  onClick={() => {
                    elimina.mutate(documento.id)
                  }}
                >
                  Elimina
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Il tipo in parole.
 *
 * Un tipo non previsto si mostra com'è invece di sparire: dice comunque
 * qualcosa, e nasconderlo farebbe sembrare che il file non ne abbia uno.
 */
function tipoLeggibile(mime: string): string {
  switch (mime) {
    case 'application/pdf':
      return 'PDF'
    case 'image/jpeg':
      return 'Immagine JPEG'
    case 'image/png':
      return 'Immagine PNG'
    case 'image/heic':
      return 'Immagine HEIC'
    case 'image/tiff':
      return 'Immagine TIFF'
    default:
      return mime
  }
}

function dimensione(byte: number): string {
  if (byte < 1024) return `${String(byte)} byte`
  if (byte < 1024 * 1024) return `${(byte / 1024).toFixed(0)} kB`

  return `${(byte / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}
